import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import { Loader2, Plus, Pencil, Trash2, X, Save, Upload, Link as LinkIcon, FileText } from "lucide-react";
import { toast } from "sonner";

// ---------- Category schemas ----------
// Each schema defines how the `data` field is edited: dynamic list or grouped inputs.
const CATEGORY_LABEL = {
  pmkp: "PMKP",
  "rawat-jalan": "10 Besar Rawat Jalan",
  "rawat-inap": "10 Besar Rawat Inap",
  kunjungan: "Kunjungan Pasien",
};

const CATEGORY_ORDER = ["pmkp", "rawat-jalan", "rawat-inap", "kunjungan"];

const emptyData = (category) => {
  switch (category) {
    case "pmkp":
      return {
        indicators: [{ name: "", unit: "%", target: 0, actual: 0 }],
        insiden: { ikp: 0, knc: 0, ktd: 0, sentinel: 0 },
      };
    case "rawat-jalan":
    case "rawat-inap":
      return {
        items: [{ rank: 1, code: "", name: "", count: 0 }],
      };
    case "kunjungan":
      return { igd: 0, rawat_jalan: 0, rawat_inap: 0, medical_check_up: 0 };
    default:
      return {};
  }
};

const now = new Date();

const LaporanAdminPanel = () => {
  const { authAxios } = useAuth();
  const [filterCat, setFilterCat] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filterCat ? { category: filterCat } : {};
      const { data } = await authAxios.get(`/admin/laporan`, { params });
      setItems(data);
    } catch (e) {
      toast.error("Gagal memuat data laporan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCat]);

  const openCreate = () => {
    const cat = filterCat || "pmkp";
    setEditing({
      _new: true,
      category: cat,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      data: emptyData(cat),
      pdf_url: "",
      note: "",
    });
  };

  const openEdit = (item) => {
    setEditing({ ...item, pdf_url: item.pdf_url || "", note: item.note || "" });
  };

  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        category: editing.category,
        year: Number(editing.year),
        month: Number(editing.month),
        data: editing.data,
        pdf_url: editing.pdf_url || null,
        note: editing.note || null,
      };
      if (editing._new) {
        await authAxios.post(`/admin/laporan`, payload);
        toast.success("Laporan berhasil ditambahkan");
      } else {
        await authAxios.put(`/admin/laporan/${editing.id}`, payload);
        toast.success("Laporan berhasil diperbarui");
      }
      close();
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus laporan ini?")) return;
    try {
      await authAxios.delete(`/admin/laporan/${id}`);
      toast.success("Laporan dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await authAxios.post(`/admin/laporan/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((prev) => ({ ...prev, pdf_url: data.url }));
      toast.success("File berhasil diunggah");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Gagal mengunggah file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-panel-laporan">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">
            Kelola Laporan
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} entri • PMKP, 10 Besar Penyakit, Kunjungan Pasien
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="admin-laporan-filter"
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
          >
            <option value="">Semua kategori</option>
            {CATEGORY_ORDER.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
            ))}
          </select>
          <button
            onClick={openCreate}
            data-testid="admin-laporan-add"
            className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white px-4 py-2.5 rounded-full text-sm font-medium shadow-md shadow-blue-900/20"
          >
            <Plus className="h-4 w-4" />
            Tambah
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-12 rounded-2xl border border-dashed border-slate-200 text-center text-slate-400">
          Belum ada laporan.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`admin-laporan-item-${item.id}`}
              className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-lg hover:border-blue-100 transition"
            >
              <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold mb-1">
                {CATEGORY_LABEL[item.category] || item.category}
              </div>
              <div className="font-heading font-semibold text-slate-900 mb-2">
                {["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"][item.month]} {item.year}
              </div>
              {item.note && (
                <div className="text-xs text-slate-500 line-clamp-2 mb-3">{item.note}</div>
              )}
              {item.pdf_url && (
                <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 mb-3">
                  <FileText className="h-3 w-3" />
                  PDF terlampir
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(item)}
                  data-testid={`admin-laporan-edit-${item.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 hover:bg-blue-100"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => del(item.id)}
                  data-testid={`admin-laporan-delete-${item.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Hapus
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <form
            onSubmit={save}
            data-testid="admin-laporan-form"
            className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-100 sticky top-0 bg-white z-10">
              <h3 className="font-heading text-xl font-bold text-slate-900">
                {editing._new ? "Tambah" : "Edit"} Laporan
              </h3>
              <button
                type="button"
                onClick={close}
                className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Meta */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Kategori</label>
                  <select
                    value={editing.category}
                    onChange={(e) => {
                      const cat = e.target.value;
                      setEditing({ ...editing, category: cat, data: emptyData(cat) });
                    }}
                    disabled={!editing._new}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500"
                  >
                    {CATEGORY_ORDER.map((c) => (
                      <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Tahun</label>
                  <input
                    type="number"
                    min="2020"
                    max="2100"
                    value={editing.year}
                    onChange={(e) => setEditing({ ...editing, year: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Bulan</label>
                  <select
                    value={editing.month}
                    onChange={(e) => setEditing({ ...editing, month: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm bg-white"
                  >
                    {["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"].map((m, i) => (
                      <option key={i + 1} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category-specific data editor */}
              {editing.category === "pmkp" && (
                <PMKPEditor
                  data={editing.data}
                  onChange={(nd) => setEditing({ ...editing, data: nd })}
                />
              )}
              {(editing.category === "rawat-jalan" || editing.category === "rawat-inap") && (
                <Top10Editor
                  data={editing.data}
                  onChange={(nd) => setEditing({ ...editing, data: nd })}
                />
              )}
              {editing.category === "kunjungan" && (
                <KunjunganEditor
                  data={editing.data}
                  onChange={(nd) => setEditing({ ...editing, data: nd })}
                />
              )}

              {/* PDF + note */}
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  Lampiran PDF / Gambar
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={editing.pdf_url}
                    onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })}
                    placeholder="/api/laporan/files/xxx.pdf atau https://..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                  />
                  <label
                    data-testid="admin-laporan-upload-label"
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border cursor-pointer transition ${
                      uploading
                        ? "bg-slate-100 text-slate-400 border-slate-200"
                        : "bg-white text-blue-800 border-blue-200 hover:bg-blue-50"
                    }`}
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Unggah
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                  </label>
                </div>
                {editing.pdf_url && (
                  <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1.5">
                    <LinkIcon className="h-3 w-3" />
                    <span className="truncate max-w-md">{editing.pdf_url}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Catatan</label>
                <textarea
                  rows={2}
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={close}
                className="px-5 py-2.5 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-laporan-save"
                className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white px-5 py-2.5 rounded-full text-sm font-medium shadow-md shadow-blue-900/20"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Simpan
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ---------- Sub-editors ----------
const PMKPEditor = ({ data, onChange }) => {
  const inds = data?.indicators || [];
  const inc = data?.insiden || { ikp: 0, knc: 0, ktd: 0, sentinel: 0 };

  const update = (patch) => onChange({ ...data, ...patch });

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-slate-600">Indikator Mutu</label>
          <button
            type="button"
            onClick={() =>
              update({
                indicators: [
                  ...inds,
                  { name: "", unit: "%", target: 0, actual: 0 },
                ],
              })
            }
            className="text-xs font-medium text-blue-800 hover:underline"
          >
            + Tambah indikator
          </button>
        </div>
        <div className="space-y-2">
          {inds.map((ind, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input
                className="col-span-5 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                placeholder="Nama indikator"
                value={ind.name}
                onChange={(e) => {
                  const next = [...inds];
                  next[i] = { ...ind, name: e.target.value };
                  update({ indicators: next });
                }}
              />
              <input
                className="col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                placeholder="Unit"
                value={ind.unit}
                onChange={(e) => {
                  const next = [...inds];
                  next[i] = { ...ind, unit: e.target.value };
                  update({ indicators: next });
                }}
              />
              <input
                type="number"
                step="0.1"
                className="col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                placeholder="Target"
                value={ind.target}
                onChange={(e) => {
                  const next = [...inds];
                  next[i] = { ...ind, target: Number(e.target.value) };
                  update({ indicators: next });
                }}
              />
              <input
                type="number"
                step="0.1"
                className="col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                placeholder="Realisasi"
                value={ind.actual}
                onChange={(e) => {
                  const next = [...inds];
                  next[i] = { ...ind, actual: Number(e.target.value) };
                  update({ indicators: next });
                }}
              />
              <button
                type="button"
                onClick={() => update({ indicators: inds.filter((_, k) => k !== i) })}
                className="col-span-1 text-red-600 hover:text-red-800 flex justify-center"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-slate-600 mb-2 block">Insiden Keselamatan Pasien</label>
        <div className="grid grid-cols-4 gap-2">
          {[
            ["ikp", "IKP"],
            ["knc", "KNC"],
            ["ktd", "KTD"],
            ["sentinel", "Sentinel"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
              <input
                type="number"
                min="0"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                value={inc[k] || 0}
                onChange={(e) => update({ insiden: { ...inc, [k]: Number(e.target.value) } })}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Top10Editor = ({ data, onChange }) => {
  const items = data?.items || [];
  const update = (nextItems) => onChange({ ...data, items: nextItems, total: nextItems.reduce((s, x) => s + Number(x.count || 0), 0) });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-slate-600">Daftar Penyakit (top 10)</label>
        <button
          type="button"
          onClick={() =>
            update([...items, { rank: items.length + 1, code: "", name: "", count: 0 }])
          }
          className="text-xs font-medium text-blue-800 hover:underline"
        >
          + Tambah baris
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input
              type="number"
              min="1"
              className="col-span-1 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              value={it.rank}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...it, rank: Number(e.target.value) };
                update(next);
              }}
            />
            <input
              className="col-span-3 px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono"
              placeholder="ICD-10"
              value={it.code}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...it, code: e.target.value };
                update(next);
              }}
            />
            <input
              className="col-span-5 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="Nama diagnosis"
              value={it.name}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...it, name: e.target.value };
                update(next);
              }}
            />
            <input
              type="number"
              min="0"
              className="col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-sm"
              placeholder="Jumlah"
              value={it.count}
              onChange={(e) => {
                const next = [...items];
                next[i] = { ...it, count: Number(e.target.value) };
                update(next);
              }}
            />
            <button
              type="button"
              onClick={() => update(items.filter((_, k) => k !== i))}
              className="col-span-1 text-red-600 hover:text-red-800 flex justify-center"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const KunjunganEditor = ({ data, onChange }) => {
  const fields = [
    { k: "igd", label: "IGD" },
    { k: "rawat_jalan", label: "Rawat Jalan" },
    { k: "rawat_inap", label: "Rawat Inap" },
    { k: "medical_check_up", label: "Medical Check-Up" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {fields.map((f) => (
        <div key={f.k}>
          <label className="text-xs font-semibold text-slate-600 mb-1.5 block">{f.label}</label>
          <input
            type="number"
            min="0"
            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
            value={data?.[f.k] || 0}
            onChange={(e) => onChange({ ...data, [f.k]: Number(e.target.value) })}
          />
        </div>
      ))}
    </div>
  );
};

export default LaporanAdminPanel;
